import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { getTheme } from '../../../shared/styles/theme';
import ApiService from '../../../shared/services/ApiService';
import { serverNowMs } from '../../../shared/utils/serverTime';

const BOX_TYPE_LABEL = { wash: 'Мойка', vacuum: 'Пылесос', air: 'Воздух' };
const TYPE_ORDER = ['wash', 'vacuum', 'air'];

const Container = styled.div`
  background: ${p => p.theme.cardBackground};
  border: 1px solid ${p => p.theme.borderColor};
  border-radius: 8px;
  padding: 16px;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
`;

const Title = styled.h2`
  margin: 0;
`;

const Button = styled.button`
  padding: 8px 14px;
  background: ${p => (p.primary ? p.theme.primaryColor : p.theme.cardBackground)};
  color: ${p => (p.primary ? 'white' : p.theme.textColor)};
  border: 1px solid ${p => (p.primary ? p.theme.primaryColor : p.theme.borderColor)};
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.95rem;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const ErrorText = styled.div`
  color: #d33;
  margin-bottom: 8px;
`;

const SuccessText = styled.div`
  color: #2e7d32;
  margin-bottom: 8px;
`;

const SectionTitle = styled.h3`
  margin: 20px 0 10px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
`;

const Card = styled.div`
  border: 1px solid ${p => p.theme.borderColor};
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: ${p => (p.active ? 'rgba(22, 163, 74, 0.08)' : p.theme.backgroundColor)};
`;

const BoxNumber = styled.div`
  font-size: 1.2rem;
  font-weight: 700;
`;

const Muted = styled.div`
  font-size: 0.85rem;
  opacity: 0.75;
`;

const Timer = styled.div`
  font-size: 1.4rem;
  font-weight: 800;
  color: ${p => (p.warn ? '#dc2626' : p.theme.primaryColor)};
  font-variant-numeric: tabular-nums;
`;

const StartButton = styled(Button)`
  width: 100%;
`;

const StopButton = styled(Button)`
  width: 100%;
  background: #dc2626;
  color: #fff;
  border-color: #dc2626;
`;

const CarPanel = styled.div`
  border: 1px solid ${p => (p.warn ? '#dc2626' : p.theme.borderColor)};
  background: ${p => (p.warn ? 'rgba(220, 38, 38, 0.06)' : p.theme.backgroundColor)};
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const CarInput = styled.input`
  padding: 8px 10px;
  border: 1px solid ${p => p.theme.borderColor};
  border-radius: 6px;
  background: ${p => p.theme.cardBackground};
  color: ${p => p.theme.textColor};
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  width: 180px;
`;

const pad2 = (n) => String(n).padStart(2, '0');

// Обратный отсчёт до expiresAt в формате MM:SS (не уходит в минус).
const countdown = (expiresIso, nowMs) => {
  if (!expiresIso) return '—';
  const end = new Date(expiresIso).getTime();
  if (Number.isNaN(end)) return '—';
  let s = Math.max(0, Math.floor((end - nowMs) / 1000));
  const m = Math.floor(s / 60);
  s -= m * 60;
  return `${pad2(m)}:${pad2(s)}`;
};

const MyWash = () => {
  const theme = getTheme('light');
  const [boxes, setBoxes] = useState([]);
  const [active, setActive] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [nowMs, setNowMs] = useState(serverNowMs());
  // Номер машины сотрудника: carNumber — сохранённый на сервере, carDraft — то, что в поле.
  const [carNumber, setCarNumber] = useState('');
  const [carRequired, setCarRequired] = useState(false);
  const [carDraft, setCarDraft] = useState('');
  const [carEditing, setCarEditing] = useState(false);
  const [carSaving, setCarSaving] = useState(false);
  const isMounted = useRef(true);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const data = await ApiService.getPersonalWashBoxes();
      if (!isMounted.current) return;
      setBoxes(Array.isArray(data?.boxes) ? data.boxes : []);
      setActive(Array.isArray(data?.active) ? data.active : []);
      setCarNumber(data?.car_number || '');
      setCarRequired(Boolean(data?.car_number_required));
      setError('');
    } catch (e) {
      if (!isMounted.current) return;
      setError('Не удалось загрузить боксы. Попробуйте обновить.');
    } finally {
      if (isMounted.current && !silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    isMounted.current = true;
    load();
    const poll = setInterval(() => load(true), 7000);
    return () => {
      isMounted.current = false;
      clearInterval(poll);
    };
  }, [load]);

  // Локальный тик раз в секунду для живых таймеров.
  useEffect(() => {
    const id = setInterval(() => setNowMs(serverNowMs()), 1000);
    return () => clearInterval(id);
  }, []);

  const saveCarNumber = async () => {
    setCarSaving(true);
    setFeedback('');
    try {
      const resp = await ApiService.setPersonalWashCarNumber(carDraft);
      setCarNumber(resp?.car_number || '');
      setCarEditing(false);
      setFeedback(resp?.message || 'Номер машины сохранён');
      setError('');
      await load(true);
    } catch (e) {
      setError(e?.response?.data?.error || 'Не удалось сохранить номер машины');
    } finally {
      setCarSaving(false);
    }
  };

  // Без номера машины включать бокс нельзя: иначе в отчёте и в уведомлении неясно, чью машину мыли.
  const carMissing = carRequired && !carNumber;

  const start = async (boxId) => {
    setBusyId(boxId);
    setFeedback('');
    try {
      const resp = await ApiService.startPersonalWash(boxId);
      setFeedback(resp?.message || 'Бокс включён');
      await load(true);
    } catch (e) {
      setError(e?.response?.data?.error || 'Не удалось включить бокс');
    } finally {
      setBusyId(null);
    }
  };

  const stop = async (boxId) => {
    setBusyId(boxId);
    setFeedback('');
    try {
      await ApiService.returnPersonalWash(boxId);
      setFeedback('Бокс возвращён в работу');
      await load(true);
    } catch (e) {
      setError(e?.response?.data?.error || 'Не удалось вернуть бокс');
    } finally {
      setBusyId(null);
    }
  };

  // Свободные боксы, сгруппированные по типу.
  const freeByType = useMemo(() => {
    const groups = {};
    for (const b of boxes) {
      if (b.status !== 'free') continue;
      const t = b.box_type || 'wash';
      if (!groups[t]) groups[t] = [];
      groups[t].push(b);
    }
    for (const t of Object.keys(groups)) {
      groups[t].sort((a, b) => (a.number ?? 0) - (b.number ?? 0));
    }
    return groups;
  }, [boxes]);

  const sortedActive = useMemo(
    () => active.slice().sort((a, b) => (a.number ?? 0) - (b.number ?? 0)),
    [active],
  );

  return (
    <Container theme={theme}>
      <HeaderRow>
        <Title>Моя мойка</Title>
        <Button theme={theme} onClick={() => load()} disabled={loading}>
          {loading ? 'Обновление…' : 'Обновить'}
        </Button>
      </HeaderRow>

      {error && <ErrorText onClick={() => setError('')}>{error}</ErrorText>}
      {feedback && <SuccessText onClick={() => setFeedback('')}>{feedback}</SuccessText>}

      {carRequired && (
        <CarPanel theme={theme} warn={carMissing}>
          {carEditing || carMissing ? (
            <>
              <div><strong>Номер моей машины</strong></div>
              <CarRow>
                <CarInput
                  theme={theme}
                  value={carDraft}
                  onChange={(e) => setCarDraft(e.target.value.toUpperCase())}
                  placeholder="А123ВС154"
                  maxLength={12}
                  autoComplete="off"
                />
                <Button primary theme={theme} onClick={saveCarNumber} disabled={carSaving || carDraft.trim().length < 5}>
                  {carSaving ? 'Сохранение…' : 'Сохранить'}
                </Button>
                {carNumber && (
                  <Button theme={theme} onClick={() => { setCarEditing(false); setCarDraft(''); }} disabled={carSaving}>
                    Отмена
                  </Button>
                )}
              </CarRow>
              <Muted>
                {carMissing
                  ? 'Пока номер не указан, включить бокс нельзя. Номер попадёт в уведомление и в отчёт по личным мойкам.'
                  : 'Номер попадёт в уведомление и в отчёт по личным мойкам.'}
              </Muted>
            </>
          ) : (
            <CarRow>
              <div>Моя машина: <strong>{carNumber}</strong></div>
              <Button theme={theme} onClick={() => { setCarDraft(carNumber); setCarEditing(true); }}>
                Изменить
              </Button>
            </CarRow>
          )}
        </CarPanel>
      )}

      {sortedActive.length > 0 && (
        <>
          <SectionTitle>Активные личные включения</SectionTitle>
          <Grid>
            {sortedActive.map((a) => {
              const secLeft = a.expires_at
                ? Math.max(0, Math.floor((new Date(a.expires_at).getTime() - nowMs) / 1000))
                : 0;
              return (
                <Card key={a.action_id || a.id || a.number} theme={theme} active>
                  <BoxNumber>Бокс №{a.number}</BoxNumber>
                  <Muted>{BOX_TYPE_LABEL[a.box_type] || a.box_type || '—'}</Muted>
                  {a.admin_username && <Muted>Кто: {a.admin_username}</Muted>}
                  <Timer theme={theme} warn={secLeft <= 60}>{countdown(a.expires_at, nowMs)}</Timer>
                  <StopButton
                    theme={theme}
                    onClick={() => stop(a.id)}
                    disabled={!a.id || busyId === a.id}
                  >
                    {busyId === a.id ? 'Выключение…' : 'Выключить сейчас'}
                  </StopButton>
                </Card>
              );
            })}
          </Grid>
        </>
      )}

      {TYPE_ORDER.filter((t) => (freeByType[t] || []).length > 0).map((t) => (
        <React.Fragment key={t}>
          <SectionTitle>{BOX_TYPE_LABEL[t]}</SectionTitle>
          <Grid>
            {freeByType[t].map((b) => {
              const remaining = b.remaining ?? 0;
              const canStart = remaining > 0 && busyId !== b.id && !carMissing;
              return (
                <Card key={b.id} theme={theme}>
                  <BoxNumber>Бокс №{b.number}</BoxNumber>
                  <Muted>Остаток лимита: {remaining} / 2 в сутки</Muted>
                  <StartButton
                    primary
                    theme={theme}
                    onClick={() => start(b.id)}
                    disabled={!canStart}
                  >
                    {busyId === b.id
                      ? 'Включение…'
                      : carMissing
                      ? 'Укажите номер машины'
                      : remaining > 0
                      ? 'Личное включение (15 мин)'
                      : 'Лимит исчерпан'}
                  </StartButton>
                </Card>
              );
            })}
          </Grid>
        </React.Fragment>
      ))}

      {!loading && sortedActive.length === 0 && TYPE_ORDER.every((t) => (freeByType[t] || []).length === 0) && (
        <Muted style={{ marginTop: 16 }}>Свободных боксов нет.</Muted>
      )}
    </Container>
  );
};

export default MyWash;
